import { motion } from 'framer-motion';
import { ArrowRight, Sparkles, ChevronDown } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';

export const Hero = () => {
  const mockupRef = useRef(null);
  const orbRef1 = useRef(null);
  const orbRef2 = useRef(null);

  useEffect(() => {
    if (mockupRef.current) {
      gsap.to(mockupRef.current, {
        y: -20,
        duration: 3,
        repeat: -1,
        yoyo: true,
        ease: 'power1.inOut',
      });
    }

    if (orbRef1.current) {
      gsap.to(orbRef1.current, {
        x: 30,
        y: -30,
        duration: 8,
        repeat: -1,
        yoyo: true,
        ease: 'power1.inOut',
      });
    }

    if (orbRef2.current) {
      gsap.to(orbRef2.current, {
        x: -30,
        y: 30,
        duration: 10,
        repeat: -1,
        yoyo: true,
        ease: 'power1.inOut',
      });
    }
  }, []);

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20"
      data-testid="hero-section"
    >
      <div
        ref={orbRef1}
        className="floating-orb w-96 h-96 bg-[#007AFF] top-20 right-20"
        style={{ position: 'absolute' }}
      />
      <div
        ref={orbRef2}
        className="floating-orb w-96 h-96 bg-[#00F0FF] bottom-20 left-20"
        style={{ position: 'absolute' }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-24">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card mb-6"
              data-testid="hero-badge"
            >
              <Sparkles className="w-4 h-4 text-[#007AFF]" />
              <span className="text-xs uppercase tracking-wider text-zinc-400 font-bold">
                Premium Web Design Agency
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-none mb-6"
              style={{ fontFamily: 'Space Grotesk, sans-serif' }}
              data-testid="hero-title"
            >
              We Build Websites That{' '}
              <span className="bg-gradient-to-r from-[#007AFF] to-[#00F0FF] bg-clip-text text-transparent glow-text">
                Convert
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-lg text-zinc-400 mb-8 max-w-xl leading-relaxed"
              data-testid="hero-description"
            >
              Modern, high-converting websites for clinics, cafés, gaming cafés, and growing
              businesses. Premium design that turns visitors into customers.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => scrollToSection('#contact')}
                className="group bg-[#007AFF] text-white px-8 py-4 rounded-full font-semibold flex items-center justify-center gap-2 hover:bg-[#0066DD] transition-all glow-border cursor-hover"
                data-testid="hero-cta-primary"
              >
                Book a Free Consultation
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => scrollToSection('#portfolio')}
                className="glass-card text-white px-8 py-4 rounded-full font-semibold hover:border-[#007AFF] transition-all cursor-hover"
                data-testid="hero-cta-secondary"
              >
                View Our Work
              </motion.button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
              className="flex items-center gap-8 mt-12"
              data-testid="hero-stats"
            >
              <div>
                <div className="text-4xl font-black text-white">50+</div>
                <div className="text-sm text-zinc-500">Projects Delivered</div>
              </div>
              <div>
                <div className="text-4xl font-black text-white">100%</div>
                <div className="text-sm text-zinc-500">Client Satisfaction</div>
              </div>
              <div>
                <div className="text-4xl font-black text-white">24/7</div>
                <div className="text-sm text-zinc-500">Support</div>
              </div>
            </motion.div>
          </motion.div>

          <motion.div
            ref={mockupRef}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.3 }}
            className="relative"
            data-testid="hero-mockup"
          >
            <div className="relative z-10 glass-card rounded-2xl p-8 transform rotate-3 hover:rotate-0 transition-transform duration-500">
              <div className="aspect-video rounded-xl overflow-hidden bg-gradient-to-br from-[#007AFF] to-[#00F0FF] p-1">
                <img
                  src="https://images.unsplash.com/photo-1753998941540-081eed4f6397?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwzfHxkYXJrJTIwbW9kZSUyMHRlY2glMjBkYXNoYm9hcmQlMjBpbnRlcmZhY2V8ZW58MHx8fHwxNzc1MDc5MzA0fDA&ixlib=rb-4.1.0&q=85"
                  alt="Premium Website Dashboard"
                  className="w-full h-full object-cover rounded-lg"
                />
              </div>
            </div>
            <div className="absolute -z-10 inset-0 bg-gradient-to-br from-[#007AFF] to-[#00F0FF] blur-3xl opacity-20" />
          </motion.div>
        </div>
      </div>

      <motion.button
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
        className="absolute bottom-10 left-1/2 transform -translate-x-1/2 cursor-hover"
        onClick={() => scrollToSection('#services')}
        data-testid="scroll-indicator"
        whileHover={{ scale: 1.2 }}
        whileTap={{ scale: 0.9 }}
      >
        <ChevronDown className="w-8 h-8 text-zinc-600" />
      </motion.button>
    </section>
  );
};